import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicHeader } from "../components/cod/PublicHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ChicagoStars, CODLogoSVG } from "../components/cod/Logo";
import { marketApi } from "../lib/api";
import { ArrowRight, ShieldCheck, Award, Briefcase, Compass, Heart, Building2 } from "lucide-react";
import { Seo } from "../components/cod/Seo";

const GROUP_META = {
  "Employment Services":     { icon: Briefcase, blurb: "Career Navigation, Resume Assistance, Interview Preparation, Job Placement, and Workforce Readiness — the full job-search stack with a COD Case Manager.", available: true },
  "Employment Requirements": { icon: ShieldCheck, blurb: "Fingerprinting, Background Checks, and PERC Assistance — the documents and clearances Chicago employers ask for.", available: false, phase: "coming_soon" },
  "Certifications":          { icon: Award, blurb: "CPR/BLS, First Aid, Food Handler, Food Manager, ServSafe Alcohol, and Security Training — job-required credentials delivered quickly.", available: false, phase: "coming_soon" },
  "Healthcare Pathways":     { icon: Heart, blurb: "CNA, PCT, Phlebotomy, EKG, Medical Coding & Billing, Home Care Aide, and Healthcare Career Navigation.", available: false, phase: "in_development" },
  "Business Services":       { icon: Building2, blurb: "Government Contract Accelerator, Business Formation Support, Capability Statements, Vendor Registration, and Procurement Readiness.", available: false, phase: "in_development" },
  "Community Services":      { icon: Compass, blurb: "Housing Navigation, Public Benefits Navigation, Behavioral Health Navigation, and Community Resource Referrals.", available: false, phase: "in_development" },
};

const PHASE_BADGE = {
  coming_soon:    { label: "Coming Soon",    bg: "#FEF3C7", fg: "#92400E" },
  in_development: { label: "In Development", bg: "#E0E7FF", fg: "#3730A3" },
};

export default function Services() {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();
  useEffect(()=>{ marketApi.opportunities().then(setServices); }, []);
  const groups = Object.keys(GROUP_META);

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Services"
        description="Employment services, employment requirements, certifications, healthcare pathways, business services, and community services offered by Chicago Opportunity Desk."
        path="/services"
        schema="ProfessionalService"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]}
      />
      <PublicHeader/>
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="cod-eyebrow">Services</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-3">The COD service catalog.</h1>
          <div className="cod-rule-red mt-5"/>
          <p className="mt-5 text-lg text-slate-700 max-w-3xl">Chicago Opportunity Desk is launching in phases. <strong>Employment &amp; Staffing is available now.</strong> The additional service groups below represent the expanding COD service model and will become active as our network and capacity expand.</p>
        </div>
      </section>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-14">
        {groups.map(g => {
          const items = services.filter(s => s.service_group === g);
          const Icon = GROUP_META[g].icon;
          const available = GROUP_META[g].available;
          const phaseBadge = available ? null : PHASE_BADGE[GROUP_META[g].phase];
          return (
            <section key={g} data-testid={`service-group-${g.toLowerCase().replace(/[^a-z]/g,"-")}`}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-md flex items-center justify-center" style={{ background: "var(--cod-navy-soft)" }}>
                  <Icon size={22} style={{color:"var(--cod-navy)"}}/>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-serif text-2xl sm:text-3xl text-slate-900">{g}</h2>
                    {available ? (
                      <Badge className="text-[10px] uppercase tracking-wider"
                             style={{background:"var(--cod-red)", color:"#fff"}}
                             data-testid={`service-group-${g.toLowerCase().replace(/[^a-z]/g,"-")}-available`}>
                        Available Now
                      </Badge>
                    ) : (
                      <Badge className="text-[10px] uppercase tracking-wider"
                             style={{background:phaseBadge.bg, color:phaseBadge.fg}}
                             data-testid={`service-group-${g.toLowerCase().replace(/[^a-z]/g,"-")}-phase`}>
                        {phaseBadge.label}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{GROUP_META[g].blurb}</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map(s => (
                  <Card key={s.id}
                    className={`cod-card-elev ${available ? "cursor-pointer" : "opacity-80 cursor-not-allowed"}`}
                    onClick={available ? (()=>navigate(`/marketplace/${s.id}`)) : undefined}
                    data-testid={`service-${s.id}`}>
                    <CardHeader>
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <Badge variant="secondary" className="w-fit capitalize">{s.category}</Badge>
                        {!available && (
                          <Badge className="text-[10px] uppercase tracking-wider"
                                 style={{background:phaseBadge.bg, color:phaseBadge.fg}}>
                            {phaseBadge.label}
                          </Badge>
                        )}
                      </div>
                      <CardTitle className="font-serif text-lg leading-snug">{s.name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-700 leading-relaxed">{s.description}</p>
                      {available ? (
                        <div className="mt-4 text-sm font-medium flex items-center gap-1" style={{color:"var(--cod-navy)"}}>Details <ArrowRight size={14}/></div>
                      ) : (
                        <div className="mt-4 text-xs text-slate-500 italic">Not yet available — service will launch as COD's network expands.</div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>
          );
        })}
        <div className="text-center pt-8">
          <Button size="lg" onClick={()=>navigate("/start?intent=find_work")} data-testid="services-find-employment-btn"
                  className="bg-[var(--cod-red)] hover:bg-[#A40D26] text-white px-8 py-7">
            Find Employment — Join Our Candidate Network <ArrowRight size={16} className="ml-2"/>
          </Button>
        </div>
      </main>
    </div>
  );
}
